import { httpsCallable } from 'firebase/functions';
import { functions } from '../platform/firebase/clients';

// Pre-submit AI validator. The expert clicks Validate in the work UI;
// this calls the server-side `validateTaskSubmission` callable, which
// runs the task type's validator and returns advisory comments inline.
//
// Validator output is TRANSIENT — comments live only in component state
// (see TaskDetailPage/viewModel.ts). They're cleared on re-validate, on
// submit, and on page unmount. They are never written to Firestore and
// never reach the human reviewer post-submit. The tradeoff: simpler
// schema and rules, in exchange for losing the validator's signal once
// the expert moves past it.

export type ValidatorSeverity = 'warning' | 'info';

export interface ValidatorComment {
  /** Same dotted-path convention as FieldCommentSlot — must match a
   *  fieldPath the task type wraps. The server's allow-list filtering
   *  guarantees this matches one of the validator's `validFieldPaths()`. */
  fieldPath: string;
  severity: ValidatorSeverity;
  text: string;
}

export interface ValidateTaskRequest {
  taskId: string;
}

export interface ValidateTaskResult {
  ok: true;
  comments: ValidatorComment[];
  /** Model id the validator used; surfaced for debug, not for users. */
  model: string;
  /** Version metadata for reproducible validator output and audits. */
  validatorPluginId?: string;
  validatorPluginVersion?: number;
  modelPolicyVersion?: string;
}

// 3 minutes — generous because Kimi K3 JSON mode can take 10-60s on a
// complex submission and the user is waiting on the result. The server
// caps at 180s; matching the client timeout means we surface the
// timeout error from one place (the server) instead of two.
const VALIDATE_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * Runs the AI validator against a task and returns the (possibly empty)
 * list of advisory comments. Throws on transport / permission errors —
 * the caller is responsible for surfacing them. An empty `comments`
 * array is a normal "no issues found" signal, not a failure.
 */
export async function validateTaskSubmission(
  req: ValidateTaskRequest,
): Promise<ValidateTaskResult> {
  const fn = httpsCallable<ValidateTaskRequest, ValidateTaskResult>(
    functions,
    'validateTaskSubmission',
    { timeout: VALIDATE_TIMEOUT_MS },
  );
  const res = await fn(req);
  return res.data;
}
